'use client';

import { faBoxesPacking, faUser } from '@fortawesome/pro-regular-svg-icons';
import { faRightFromBracket } from '@fortawesome/pro-solid-svg-icons';
import { FC } from 'react';
import UserInitials from '@/components/account/user-initials/user-initials';
import {
  Dropdown,
  DropdownItem,
  DropdownMenu,
  DropdownSection,
  DropdownTrigger,
  Icon,
} from '@/components/ui';
import useLocale from '@/hooks/use-locale';
import useNavigate from '@/hooks/use-navigate';
import { useTranslation } from '@/providers/app-provider/app-provider';
import { useKeycloak } from '@/providers/keycloak-provider';
import { cn } from '@/utils';

export type UserButtonProps = {
  desktopBreakpoint?: 'lg' | 'xl';
  className?: string;
};

const UserButton: FC<UserButtonProps> = ({ desktopBreakpoint = 'lg', className }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { keycloak } = useKeycloak();
  const locale = useLocale();
  const isXlBreakpoint = desktopBreakpoint === 'xl';

  return (
    <Dropdown>
      <DropdownTrigger>
        <button
          type="button"
          aria-label={`User menu for ${keycloak?.idTokenParsed?.name ?? 'account'}`}
          className={cn(
            'inline-flex cursor-pointer items-center gap-2 rounded-xl border-0 bg-transparent p-1 text-left hover:bg-gray-100',
            isXlBreakpoint ? 'xl:w-[200px] xl:p-2' : 'lg:w-[200px] lg:p-2',
            className,
          )}
        >
          <UserInitials
            size="sm"
            className={cn('h-7 w-7 shrink-0', isXlBreakpoint ? 'xl:h-9 xl:w-9' : 'lg:h-9 lg:w-9')}
          />

          <div
            className={cn('hidden min-w-0 flex-1 flex-col', isXlBreakpoint ? 'xl:flex' : 'lg:flex')}
          >
            <span className="truncate text-sm font-semibold !text-gray-600">
              {keycloak?.idTokenParsed?.name}
            </span>
            <span className="truncate text-sm text-gray-500">{keycloak?.idTokenParsed?.email}</span>
          </div>
        </button>
      </DropdownTrigger>
      <DropdownMenu>
        <DropdownSection>
          <DropdownItem
            key="account"
            textValue="account"
            startContent={<Icon icon={faUser} />}
            onPress={() => navigate('/account')}
          >
            {t?.common.layout.userProfile}
          </DropdownItem>
          <DropdownItem
            key="orders"
            textValue="orders"
            startContent={<Icon icon={faBoxesPacking} />}
            onPress={() => navigate('/orders')}
          >
            {t?.common.layout.myOrders}
          </DropdownItem>
          <DropdownItem
            key="logout"
            textValue="logout"
            startContent={<Icon icon={faRightFromBracket} />}
            className="text-danger"
            color="danger"
            onPress={() => keycloak?.logout({ redirectUri: `${window.location.origin}/${locale}` })}
          >
            {t?.common.layout.logOut}
          </DropdownItem>
        </DropdownSection>
      </DropdownMenu>
    </Dropdown>
  );
};

export default UserButton;
